import { useEffect, useRef } from 'react'

interface Star {
  x: number
  y: number
  size: number
  opacity: number
  pulsePhase: number
  pulseSpeed: number
}

interface StarfieldProps {
  intensity?: 'low' | 'medium' | 'high'
}

const FRAME_INTERVAL_60 = 1000 / 60
const FRAME_INTERVAL_30 = 1000 / 30

const Starfield = ({ intensity = 'low' }: StarfieldProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const starsRef = useRef<Star[]>([])
  const animationFrameRef = useRef<number | undefined>(undefined)
  const throttledRef = useRef(false)
  const lastFrameTimeRef = useRef(0)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    // Initialize stars
    const starCount = intensity === 'high' ? 300 : intensity === 'medium' ? 200 : 150
    if (starsRef.current.length === 0) {
      starsRef.current = Array.from({ length: starCount }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.5 + 0.3,
        pulsePhase: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.02 + 0.01,
      }))
    }

    // Listen for terminal input focus/blur to throttle
    const handleFocusIn = (e: FocusEvent) => {
      if ((e.target as HTMLElement)?.id === 'terminal-input') {
        throttledRef.current = true
      }
    }
    const handleFocusOut = (e: FocusEvent) => {
      if ((e.target as HTMLElement)?.id === 'terminal-input') {
        throttledRef.current = false
      }
    }
    document.addEventListener('focusin', handleFocusIn)
    document.addEventListener('focusout', handleFocusOut)

    // Pre-create reusable gradient cache for large stars
    // Gradients are recreated when star positions shift (high intensity distortion),
    // but for low/medium we can cache them since stars don't move
    const gradientCache = new Map<number, CanvasGradient>()

    const buildGradient = (star: Star, xOffset: number): CanvasGradient => {
      const gradient = ctx.createRadialGradient(
        star.x + xOffset, star.y, 0,
        star.x + xOffset, star.y, star.size * 3,
      )
      gradient.addColorStop(0, 'rgba(136, 204, 255, 0.3)')
      gradient.addColorStop(1, 'rgba(136, 204, 255, 0)')
      return gradient
    }

    // Pre-cache gradients for non-moving stars (low/medium intensity)
    if (intensity !== 'high') {
      starsRef.current.forEach((star, idx) => {
        if (star.size > 1.5) {
          gradientCache.set(idx, buildGradient(star, 0))
        }
      })
    }

    // Animation loop with frame-time gating
    let frame = 0
    const animate = (timestamp: number) => {
      const interval = throttledRef.current ? FRAME_INTERVAL_30 : FRAME_INTERVAL_60
      const elapsed = timestamp - lastFrameTimeRef.current

      if (elapsed < interval) {
        animationFrameRef.current = requestAnimationFrame(animate)
        return
      }
      lastFrameTimeRef.current = timestamp - (elapsed % interval)

      ctx.fillStyle = '#020803'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      starsRef.current.forEach((star, idx) => {
        star.pulsePhase += star.pulseSpeed
        const pulseOpacity = Math.sin(star.pulsePhase) * 0.3 + 0.7
        const finalOpacity = star.opacity * pulseOpacity

        let distortion = 0
        if (intensity === 'high') {
          distortion = Math.sin(frame * 0.01 + star.pulsePhase) * 2
        }

        ctx.fillStyle = `rgba(255, 255, 255, ${finalOpacity})`
        ctx.beginPath()
        ctx.arc(star.x + distortion, star.y, star.size, 0, Math.PI * 2)
        ctx.fill()

        if (star.size > 1.5) {
          let gradient: CanvasGradient
          if (intensity === 'high') {
            gradient = buildGradient(star, distortion)
          } else {
            gradient = gradientCache.get(idx)!
          }
          // Modulate opacity by adjusting globalAlpha
          ctx.globalAlpha = finalOpacity
          ctx.fillStyle = gradient
          ctx.beginPath()
          ctx.arc(star.x + distortion, star.y, star.size * 3, 0, Math.PI * 2)
          ctx.fill()
          ctx.globalAlpha = 1.0
        }
      })

      frame++
      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animationFrameRef.current = requestAnimationFrame(animate)

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      document.removeEventListener('focusin', handleFocusIn)
      document.removeEventListener('focusout', handleFocusOut)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [intensity])

  return (
    <canvas
      ref={canvasRef}
      className="starfield-canvas"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  )
}

export default Starfield
